import { useState, useEffect } from "react";
import {
  createExhibit,
  updateExhibit,
  uploadExhibitImages,
  deleteExhibitImage,
  type Exhibit,
  type ExhibitImage,
  type HeaderVariant,
} from "@/lib/api";
import { ImageEditor } from "./ImageEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X, Upload } from "lucide-react";
import { colors, fonts, shadows } from "@/lib/theme";

interface ExhibitModalProps {
  exhibit: Exhibit | null;
  pin: string;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for creating and editing exhibits
 * Handles exhibit metadata and image management
 */
const HEADER_VARIANTS: { value: HeaderVariant; label: string }[] = [
  { value: "default", label: "Default (Subtle Gradient)" },
  { value: "expanding-lines", label: "Expanding Lines" },
  { value: "growing-circle", label: "Growing Circle" },
  { value: "organic-strokes", label: "Organic Brushstrokes" },
  { value: "minimal-lines", label: "Minimal Vertical Lines" },
  { value: "elegant-frame", label: "Elegant Frame" },
];

export function ExhibitModal({ exhibit, pin, onClose, onSuccess }: ExhibitModalProps) {
  const [title, setTitle] = useState(exhibit?.title || "");
  const [description, setDescription] = useState(exhibit?.description || "");
  const [headerVariant, setHeaderVariant] = useState<HeaderVariant>(exhibit?.headerVariant || "default");
  const [visible, setVisible] = useState(exhibit?.visible ?? true);
  const [existingImages, setExistingImages] = useState<ExhibitImage[]>(exhibit?.images || []);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  // Generate previews for selected files
  useEffect(() => {
    if (selectedFiles.length === 0) {
      setPreviews([]);
      return;
    }

    const newPreviews: string[] = [];
    selectedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        if (newPreviews.length === selectedFiles.length) {
          setPreviews([...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });
  }, [selectedFiles]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    setError("");
  };

  const removeNewFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExisting = async (imageId: number) => {
    if (!exhibit || !confirm("Remove this image?")) return;

    try {
      await deleteExhibitImage(exhibit.id, imageId, pin);
      setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
    } catch (err) {
      console.error("Failed to delete image:", err);
      setError("Failed to delete image");
    }
  };

  const handleImageUpdate = (updated: ExhibitImage) => {
    setExistingImages((prev) => prev.map((img) => (img.id === updated.id ? updated : img)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setUploading(true);

    try {
      let exhibitId: number;

      if (exhibit) {
        await updateExhibit(
          exhibit.id,
          { title: title || null, description: description || null, headerVariant, visible },
          pin
        );
        exhibitId = exhibit.id;
      } else {
        if (selectedFiles.length === 0) {
          setError("At least one image is required");
          setUploading(false);
          return;
        }

        const newExhibit = await createExhibit(
          { title: title || null, description: description || null, headerVariant, visible },
          pin
        );
        exhibitId = newExhibit.id;
      }

      if (selectedFiles.length > 0) {
        await uploadExhibitImages(exhibitId, selectedFiles, pin);
      }

      onSuccess();
    } catch (err) {
      console.error("Save error:", err);
      setError("Failed to save exhibit. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div
        className="rounded-lg max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: colors.surface, boxShadow: shadows.cardHover }}
      >
        {/* Header */}
        <ModalHeader title={exhibit ? "Edit Exhibit" : "New Exhibit"} onClose={onClose} />

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Exhibit Title */}
          <div>
            <Label htmlFor="title">Exhibit Title (optional)</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter exhibit title"
            />
            <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
              Shown as a header before the images
            </p>
          </div>

          {/* Exhibit Description */}
          <div>
            <Label htmlFor="description">Exhibit Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              rows={2}
            />
          </div>

          {/* Header Style */}
          <div>
            <Label htmlFor="headerVariant">Header Style</Label>
            <select
              id="headerVariant"
              value={headerVariant}
              onChange={(e) => setHeaderVariant(e.target.value as HeaderVariant)}
              className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
              style={{ borderColor: colors.border, color: colors.text }}
            >
              {HEADER_VARIANTS.map((variant) => (
                <option key={variant.value} value={variant.value}>
                  {variant.label}
                </option>
              ))}
            </select>
            <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
              Animation style for the exhibit header section
            </p>
          </div>

          {/* Visibility Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="visible"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              className="w-4 h-4"
            />
            <Label htmlFor="visible" className="cursor-pointer">
              Visible on gallery
            </Label>
          </div>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div>
              <Label className="mb-3 block">Images ({existingImages.length})</Label>
              <div className="space-y-3">
                {existingImages.map((img) => (
                  <ImageEditor
                    key={img.id}
                    image={img}
                    exhibitId={exhibit!.id}
                    pin={pin}
                    onDelete={() => handleDeleteExisting(img.id)}
                    onUpdate={handleImageUpdate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add New Images */}
          <ImageUploader
            selectedFiles={selectedFiles}
            previews={previews}
            isNewExhibit={!exhibit}
            onFileChange={handleFileChange}
            onRemoveFile={removeNewFile}
          />

          {/* Error Message */}
          {error && (
            <p className="text-sm" style={{ color: colors.accent }}>
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={uploading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploading}
              className="flex-1"
              style={{ backgroundColor: colors.primary, color: colors.surface }}
            >
              {uploading ? "Saving..." : exhibit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Modal header with title and close button
 */
interface ModalHeaderProps {
  title: string;
  onClose: () => void;
}

function ModalHeader({ title, onClose }: ModalHeaderProps) {
  return (
    <div
      className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10"
      style={{ borderColor: colors.border }}
    >
      <h2 className="text-2xl" style={{ fontFamily: fonts.serif, color: colors.text }}>
        {title}
      </h2>
      <button onClick={onClose} className="hover:opacity-70">
        <X size={24} style={{ color: colors.textMuted }} />
      </button>
    </div>
  );
}

/**
 * Image upload section with previews
 */
interface ImageUploaderProps {
  selectedFiles: File[];
  previews: string[];
  isNewExhibit: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
}

function ImageUploader({
  selectedFiles,
  previews,
  isNewExhibit,
  onFileChange,
  onRemoveFile,
}: ImageUploaderProps) {
  return (
    <div>
      <Label htmlFor="images">Add Images {isNewExhibit && "*"}</Label>
      <div className="mt-2">
        <label
          htmlFor="images"
          className="flex items-center justify-center gap-2 p-6 border-2 border-dashed rounded cursor-pointer hover:bg-gray-50 transition-colors"
          style={{ borderColor: colors.border }}
        >
          <Upload size={20} style={{ color: colors.textMuted }} />
          <span style={{ color: colors.textMuted }}>
            {selectedFiles.length > 0
              ? `${selectedFiles.length} new file(s) selected`
              : "Choose images"}
          </span>
        </label>
        <input
          id="images"
          type="file"
          accept="image/*"
          multiple
          onChange={onFileChange}
          className="hidden"
        />
      </div>

      {/* Preview grid */}
      {previews.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mt-4">
          {previews.map((preview, index) => (
            <div key={index} className="relative aspect-square rounded overflow-hidden group">
              <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onRemoveFile(index)}
                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
        After uploading, expand each image to add its title and caption
      </p>
    </div>
  );
}
